<template>
  <!-- fleet assistant brief tile for the today board. self-contained like
       huddle_panel: loads per tenant, refetches when the board signature
       changes, and renders nothing at all when the toggle or fleet
       assistant is off for the tenant. the server owns the fingerprint
       cache; this component never asks it to generate except through the
       normal load (or an explicit admin regenerate). -->
  <div v-if="enabled === true" class="obr-tile">
    <div class="obr-head">
      <div class="obr-title">
        <svg class="obr-glyph" viewBox="0 0 24 24" aria-hidden="true">
          <path d="M6 3h12a3 3 0 0 1 3 3v8a3 3 0 0 1-3 3h-4l-5 4v-4H6a3 3 0 0 1-3-3V6a3 3 0 0 1 3-3z" fill="none" stroke="currentColor" stroke-width="1.75" stroke-linejoin="round"/>
          <circle cx="8.5" cy="10.25" r="1.25" fill="currentColor"/>
          <circle cx="12" cy="10.25" r="1.25" fill="currentColor"/>
          <circle cx="15.5" cy="10.25" r="1.25" fill="currentColor"/>
        </svg>
        <span>Fleet Assistant Brief</span>
      </div>
      <div class="obr-head-meta">
        <span v-if="overLimit && brief" class="obr-stale-chip">From earlier today</span>
        <span v-if="generatedLabel" class="obr-stamp">Generated {{ generatedLabel }}</span>
        <b-button
          v-if="canManage"
          size="sm"
          variant="outline-secondary"
          class="obr-regen"
          :disabled="loading"
          title="Regenerate the brief"
          @click="load({ force: true })"
        >
          <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
        </b-button>
        <b-button
          size="sm"
          variant="outline-secondary"
          class="obr-collapse"
          :title="collapsed ? 'Expand the brief' : 'Collapse the brief'"
          :aria-expanded="(!collapsed).toString()"
          @click="toggleCollapsed"
        >
          <i class="fa" :class="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></i>
        </b-button>
      </div>
    </div>

    <!-- a collapsed tile is just the head row; loading keeps running
         underneath since it is signature-driven and cheap. -->
    <div v-show="!collapsed" class="obr-collapse-wrap">
      <!-- body states, in priority order: shimmer while a generation is in
           flight (here or on another viewer's request), then the brief,
           then the budget-paused note, then a quiet unavailable line. -->
      <div v-if="showShimmer" class="obr-shimmer" aria-hidden="true">
        <div class="obr-shimmer-line"></div>
        <div class="obr-shimmer-line obr-shimmer-line--mid"></div>
        <div class="obr-shimmer-line obr-shimmer-line--short"></div>
      </div>
      <template v-else-if="brief">
        <div class="obr-body" @click="handleRenderedLinkClick" v-html="renderedBrief"></div>
        <div v-if="overLimit" class="obr-note">
          <i class="fa fa-pause-circle-o"></i>&nbsp;Monthly AI budget reached, so the brief is paused.
        </div>
      </template>
      <div v-else-if="overLimit" class="obr-note">
        <i class="fa fa-pause-circle-o"></i>&nbsp;Monthly AI budget reached, so the brief is paused.
      </div>
      <!-- load failure: retry is a free cached GET, so the link is open to
           everyone; only regenerate stays manage-gated. -->
      <div v-else-if="loadError" class="obr-empty">
        Brief did not load.
        <button type="button" class="obr-retry" @click="load()">Retry</button>
      </div>
      <div v-else class="obr-empty">The brief is not available right now.</div>

      <div v-if="canDiscuss && brief" class="obr-actions">
        <b-button size="sm" variant="outline-secondary" class="obr-discuss" @click="discuss">
          <i class="fa fa-comments-o"></i>&nbsp;Discuss in Fleet Assistant
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import moment from 'moment/moment'
import { formatDateTimeCompact, formatTimeCompact } from '../helpers/dates'
import { get_ops_desk_board_brief } from '../networking/apis'
import { renderMarkdownToHtml } from '../utils/markdown_renderer'

export default {
  name: 'fa_brief_tile',
  props: {
    tenant: { type: String, default: '' },
    // a cheap client-side hash of the board payload, recomputed by
    // ops_board on every fetch. the tile only re-asks the server when it
    // changes, so the 60s tick costs nothing while the board is quiet.
    boardSignature: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      enabled: null,
      collapsed: false,
      brief: null,
      overLimit: false,
      generating: false,
      loadError: false,
      lastLoadedSignature: '',
      fetch_seq: 0,
      retry_timer: null,
    }
  },

  computed: {
    ...mapState({
      user: state => state.profile.user,
    }),
    // regenerate matches the huddle panel's manage gate (and the server
    // ignores force from anyone else anyway).
    canManage() {
      const role = this.user && this.user.role
      return role === 'admin' || role === 'customer-admin'
    },
    // mirror of the App.vue launcher gate so the discuss button never
    // emits into a void (no launcher mounted means no listener).
    canDiscuss() {
      const u = this.user
      if (!u) return false
      const roleOk = ['admin', 'customer', 'customer-admin', 'manager', 'dispatch'].includes(u.role) || !!u.fleet_assistant_access
      const features = (u.features && u.features[this.tenant]) || null
      const featureOk = !features || features.fleet_assistant_enabled !== false
      const hidden = u.preferences && u.preferences.hide_fleet_assistant_launcher
      return roleOk && featureOk && !hidden
    },
    showShimmer() {
      return (this.loading || this.generating) && !this.brief
    },
    renderedBrief() {
      if (!this.brief || !this.brief.markdown) return ''
      return renderMarkdownToHtml(this.brief.markdown, { addLastElementClass: true })
    },
    generatedLabel() {
      const at = this.brief && this.brief.generated_at
      if (!at) return ''
      // a cached brief can outlive its day (over-limit edge); claim the
      // date whenever the stamp is not from today
      const m = moment(String(at))
      if (m.isValid() && !m.isSame(moment(), 'day')) return formatDateTimeCompact(at)
      return formatTimeCompact(at)
    },
  },

  watch: {
    // the navbar tenant switcher swaps ?tenant= without remounting
    tenant() {
      // optimistic seed from the last known answer, same as created()
      this.enabled = this.readEnabledCache() ? true : null
      this.collapsed = this.readCollapsedCache()
      this.brief = null
      this.overLimit = false
      this.generating = false
      this.load()
    },
    boardSignature(next) {
      if (next && next !== this.lastLoadedSignature) this.load()
    },
  },

  created() {
    // optimistic seed from the last known enabled state kills the layout
    // pop-in: known-enabled tenants render the shimmer instantly while
    // load() runs, and a wrong guess self-corrects on response (the tile
    // collapses or appears once the server answers).
    this.enabled = this.readEnabledCache() ? true : null
    this.collapsed = this.readCollapsedCache()
  },

  mounted() {
    this.load()
  },

  beforeDestroy() {
    this.clearRetry()
  },

  methods: {
    // localStorage helpers are try/catch wrapped so private mode (or a
    // blocked storage api) degrades to the old always-refetch behavior.
    readEnabledCache() {
      try {
        return localStorage.getItem('obr_enabled:' + this.tenant) === '1'
      } catch (e) {
        return false
      }
    },

    writeEnabledCache(val) {
      try {
        localStorage.setItem('obr_enabled:' + this.tenant, val ? '1' : '0')
      } catch (e) {
        // private mode; nothing to do
      }
    },

    readCollapsedCache() {
      try {
        return localStorage.getItem('obr_collapsed:' + this.tenant) === '1'
      } catch (e) {
        return false
      }
    },

    toggleCollapsed() {
      this.collapsed = !this.collapsed
      try {
        localStorage.setItem('obr_collapsed:' + this.tenant, this.collapsed ? '1' : '0')
      } catch (e) {
        // private mode; nothing to do
      }
    },

    clearRetry() {
      if (this.retry_timer) {
        clearTimeout(this.retry_timer)
        this.retry_timer = null
      }
    },

    // errors degrade quietly: the tile keeps whatever it has and the
    // board itself is never blocked.
    async load({ force = false } = {}) {
      if (!this.tenant) return
      this.clearRetry()
      const seq = ++this.fetch_seq
      this.loading = true
      this.loadError = false
      this.lastLoadedSignature = this.boardSignature
      try {
        const res = await get_ops_desk_board_brief(this.tenant, force)
        if (seq !== this.fetch_seq) return
        const data = (res && res.value && res.value.data) || {}
        this.enabled = data.enabled === true
        this.writeEnabledCache(data.enabled === true)
        this.brief = data.brief || null
        this.overLimit = !!data.over_limit
        this.generating = !!data.generating
        if (this.generating) {
          // another viewer holds the generation lock; check back once
          this.retry_timer = setTimeout(() => this.load(), 8000)
        }
      } catch (e) {
        if (seq === this.fetch_seq) {
          // surface the failure, and clear the signature so the watcher
          // refires; leaving it set would make this state sticky for
          // viewers without the regenerate button
          this.loadError = true
          this.lastLoadedSignature = ''
        }
      } finally {
        if (seq === this.fetch_seq) this.loading = false
      }
    },

    discuss() {
      this.$root.$emit('open-fleet-assistant', {
        initialQuery: 'Walk me through today\'s brief and help me decide what to handle first.',
      })
    },

    // rendered markdown anchors carry data-spa-nav / data-help-article;
    // intercept so they route through the spa instead of a full page load.
    handleRenderedLinkClick(event) {
      const anchor = event.target.closest('a[data-spa-nav], a[data-help-article]')
      if (!anchor) return
      event.preventDefault()
      const href = anchor.getAttribute('href')
      if (href && this.$router) this.$router.push(href).catch(() => {})
    },
  },
}
</script>

<style scoped>
/* tile chrome matches the board's .ob-tile, plus the fleet assistant blue
   accent so the ai surface reads as distinct at a glance. */
.obr-tile {
  display: flex;
  flex-direction: column;
  gap: 8px;
  /* one 14px gutter below; the rail's 14px bottom margin owns the top,
     keeping the board's 14px rhythm (no own top margin) */
  margin-bottom: 14px;
  padding: 12px 14px;
  background: var(--fi-insight-paper);
  border: 1px solid var(--fi-insight-paper-border);
  border-left: 3px solid #007ac7;
  border-radius: 8px;
  text-align: left;
  font-size: 14px;
  color: var(--fi-insight-ink);
}
.obr-head {
  display: flex; align-items: center; justify-content: space-between;
  gap: 12px; flex-wrap: wrap;
}
.obr-title {
  font-size: 12px; font-weight: 700; color: var(--fi-insight-ink-muted);
  text-transform: uppercase; letter-spacing: 0.4px;
  display: inline-flex; align-items: center; gap: 6px;
}
.obr-glyph { width: 16px; height: 16px; color: #007ac7; flex-shrink: 0; }
.obr-head-meta { display: inline-flex; align-items: center; gap: 8px; flex-wrap: wrap; }
.obr-stamp { font-size: 12px; color: var(--fi-insight-ink-muted); }
.obr-stale-chip {
  font-size: 11px; font-weight: 600; color: #7a5400;
  background: #fff4d1; border: 1px solid #f0d97a;
  border-radius: 999px; padding: 1px 8px;
}
.obr-regen { padding: 2px 8px; }
.obr-collapse { padding: 2px 8px; }

/* keep the root's 8px column rhythm inside the collapse wrapper */
.obr-collapse-wrap { display: flex; flex-direction: column; gap: 8px; }

/* cap-and-scroll: a long narrative cannot push the needs-attention tile
   below the fold. actions and header stay outside the scroll. */
.obr-body { font-size: 14px; line-height: 1.5; max-height: 280px; overflow-y: auto; }
.obr-body ::v-deep p { margin: 0 0 6px; }
.obr-body ::v-deep p:last-child { margin-bottom: 0; }
.obr-body ::v-deep ul { margin: 0 0 6px; padding-left: 20px; }
.obr-body ::v-deep li { margin: 2px 0; }

.obr-note {
  font-size: 12.5px; color: #7a5400;
  background: #fff8ef; border: 1px solid #f0d9b5;
  border-radius: 4px; padding: 6px 10px;
}
.obr-empty { font-size: 12.5px; color: var(--fi-insight-ink-muted); }
.obr-retry {
  background: none; border: none; padding: 0;
  font-size: 12.5px; color: var(--fi-ops-accent-strong);
  text-decoration: underline; cursor: pointer;
}
.obr-retry:focus-visible { outline: 2px solid var(--fi-ops-accent); outline-offset: 2px; }

/* placeholder lines while a generation is in flight */
.obr-shimmer { display: flex; flex-direction: column; gap: 7px; padding: 2px 0; }
.obr-shimmer-line {
  height: 12px; border-radius: 6px; width: 92%;
  background: linear-gradient(90deg, #eef2f6 25%, #f8fafc 37%, #eef2f6 63%);
  background-size: 400% 100%;
  animation: obr-shimmer 1.4s ease infinite;
}
.obr-shimmer-line--mid { width: 78%; }
.obr-shimmer-line--short { width: 55%; }
@keyframes obr-shimmer {
  0% { background-position: 100% 0; }
  100% { background-position: 0 0; }
}

.obr-actions { display: flex; }
.obr-discuss { color: #007ac7; border-color: #6cb1de; }
.obr-discuss:hover, .obr-discuss:focus {
  color: #005f9e; background: #e8f4fd; border-color: #6cb1de;
}
</style>
